/**
 * A agenda da área — o instante de um evento, e o fuso em que ele foi marcado.
 *
 * ## O problema que este arquivo existe para resolver
 *
 * **"Live dia 12 às 20h" não é um instante.** É uma hora de parede, e uma hora
 * de parede só vira instante quando alguém diz *20h de onde*. O tutor está em
 * São Paulo, um aluno em Lisboa e outro em Manaus: os três leem "20h" e os três
 * chegam em momentos diferentes. Um evento que aparece na hora errada é pior que
 * evento nenhum — a pessoa perde a live e culpa a plataforma, não o fuso.
 *
 * ## A decisão, e por que são DOIS campos e não um
 *
 * O banco guarda **o instante** (`timestamptz`, portanto UTC) **e o fuso em que
 * ele foi marcado** (`America/Sao_Paulo`), porque os dois respondem perguntas
 * diferentes:
 *
 * - **o instante** diz *quando acontece*. É o que se compara com `now()`, o que
 *   ordena a agenda, o que decide se a live já começou. É absoluto.
 * - **o fuso** diz *o que o tutor quis dizer*. É o que se mostra a ele ao
 *   editar: quem marcou "20h" precisa reabrir a ficha e ler "20h", não "23h" —
 *   e sem o fuso guardado não há como reconstruir a hora de parede original.
 *
 * Guardar só o instante perde a intenção; guardar só a hora local perde a ordem.
 * Guardar os dois custa uma coluna `text` e fecha o buraco dos dois lados.
 *
 * ## O que o ALUNO vê
 *
 * O horário **no fuso dele**, com o nome do fuso ao lado (`20:00 BRT`,
 * `00:00 WEST`). O nome não é enfeite: sem ele, quem viaja não tem como saber se
 * a tela já converteu ou se está mostrando a hora do organizador.
 *
 * ## Por que nenhuma dependência nova
 *
 * `Intl.DateTimeFormat` com `timeZone` resolve os dois sentidos da conversão
 * usando a base de fusos do próprio runtime — a mesma que o Postgres tem, e a
 * mesma que o navegador do aluno tem. Uma biblioteca de datas traria a tabela de
 * fusos junto, e uma tabela congelada num `package-lock` é como um evento
 * marcado para depois de uma mudança de horário de verão sai errado.
 *
 * ## Fora de escopo, de propósito: RECORRÊNCIA
 *
 * Não há "toda terça". Recorrência é um problema maior do que parece —
 * exceções ("esta semana não tem"), mudança de horário de verão no meio da
 * série (a terça das 20h vira das 21h para metade dos alunos), e "editar só
 * esta ocorrência" versus "editar a série toda". Evento avulso primeiro; a
 * recorrência entra quando alguém pedir, com o caso real na mão.
 */

import { tradutorDe, type Tradutor } from '../i18n/tela.js';

/** Os limites de duração. Quinze minutos a doze horas — ver `conferirEvento`. */
export const DURACAO_MINIMA = 5;
export const DURACAO_MAXIMA = 12 * 60;

/** O fuso padrão quando o operador não escolheu outro. */
export const FUSO_PADRAO = 'America/Sao_Paulo';

// ───────────────────────────────────────────────────────────────── conversão

/**
 * O deslocamento do fuso, em minutos a leste de UTC, NAQUELE instante.
 *
 * "Naquele instante" é a parte que importa: `America/Sao_Paulo` é −180 hoje e
 * foi −120 em janeiro de 2018, e `Europe/Lisbon` alterna duas vezes por ano. Um
 * deslocamento constante por fuso é a forma mais comum de errar isto.
 *
 * O truque é o único que funciona sem tabela própria: formata-se o instante
 * **no fuso pedido** com as partes numéricas, remonta-se essa leitura como se
 * fosse UTC, e a diferença para o instante original é o deslocamento.
 */
export function deslocamentoDoFuso(fuso: string, instante: Date): number {
  const partes = new Intl.DateTimeFormat('en-US', {
    timeZone: fuso,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instante);

  const p: Record<string, string> = {};
  for (const parte of partes) if (parte.type !== 'literal') p[parte.type] = parte.value;

  /* `hour12: false` devolve 24 para a meia-noite em alguns runtimes; 24 não
     existe em `Date.UTC` como hora do MESMO dia, e sem esta linha a virada da
     meia-noite ficava um dia à frente. */
  const hora = p.hour === '24' ? 0 : Number(p.hour);

  const comoSeFosseUtc = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    hora,
    Number(p.minute),
    Number(p.second),
  );

  /* Milissegundos fora da conta dos dois lados: o `formatToParts` não os
     devolve, e mantê-los no minuendo produziria um deslocamento quebrado. */
  const semMilissegundos = Math.floor(instante.getTime() / 1000) * 1000;

  return Math.round((comoSeFosseUtc - semMilissegundos) / 60_000);
}

/** `YYYY-MM-DDTHH:mm` — o formato do `<input type="datetime-local">`. */
const LOCAL = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/;

const UM_DIA = 86_400_000;

/**
 * A hora de parede que o tutor digitou vira o instante em que a live acontece.
 *
 * ## Por que dois candidatos, e não uma correção
 *
 * O deslocamento depende do instante, e o instante é o que se está procurando —
 * a definição é circular. A saída ingênua (chutar, medir, corrigir) acerta os
 * dias normais e erra exatamente nos dois dias do ano em que alguém pode marcar
 * uma live e reclamar.
 *
 * Então: pegam-se os deslocamentos **um dia antes** e **um dia depois** da hora
 * pedida, que num dia de virada são os dois lados dela. Cada um produz um
 * candidato a instante, e um candidato só é válido se, medido de novo, ele
 * devolver o deslocamento que o gerou. Isso classifica os três casos sem
 * ambiguidade.
 *
 * ## A hora que existe duas vezes, e a que não existe
 *
 * Na madrugada em que o relógio ATRASA, `01:30` acontece duas vezes — os dois
 * candidatos são válidos, e esta função devolve **o primeiro**. É o que se
 * espera de "às 1h30": a live começa na primeira vez que der 1h30.
 *
 * Na madrugada em que o relógio ADIANTA, `02:30` não existe — nenhum candidato é
 * válido, e esta função devolve o que o deslocamento ANTERIOR produz, que cai em
 * `03:30`. É a mesma escolha do padrão `compatible` do ECMAScript Temporal e do
 * que fazem Google Calendar e Luxon: a live acontece no instante seguinte ao
 * buraco, e não uma hora antes dele.
 *
 * A tela mostra ao operador a hora resultante já convertida de volta, então uma
 * escolha assim fica visível antes de ele salvar.
 */
export function instanteDoLocal(local: string, fuso: string): Date {
  const m = LOCAL.exec(local.trim());
  if (!m) throw new Error(`Data e hora inválidas: "${local}". Use AAAA-MM-DDTHH:MM.`);

  const comoUtc = Date.UTC(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4]),
    Number(m[5]),
    Number(m[6] ?? '0'),
  );

  const antes = deslocamentoDoFuso(fuso, new Date(comoUtc - UM_DIA));
  const depois = deslocamentoDoFuso(fuso, new Date(comoUtc + UM_DIA));

  const candidatoAntes = comoUtc - antes * 60_000;
  const candidatoDepois = comoUtc - depois * 60_000;

  const valeAntes = deslocamentoDoFuso(fuso, new Date(candidatoAntes)) === antes;
  const valeDepois = deslocamentoDoFuso(fuso, new Date(candidatoDepois)) === depois;

  /* Os dois valem: a hora aconteceu duas vezes. A primeira é a que se marcou. */
  if (valeAntes && valeDepois) return new Date(Math.min(candidatoAntes, candidatoDepois));
  if (valeAntes) return new Date(candidatoAntes);
  if (valeDepois) return new Date(candidatoDepois);

  /* Nenhum vale: a hora não existe. O candidato do deslocamento anterior cai
     depois do buraco — que é para onde a live é empurrada. */
  return new Date(candidatoAntes);
}

/**
 * O caminho de volta: o instante vira a hora de parede daquele fuso.
 *
 * É o que a ficha do operador mostra — ele marcou "20h em São Paulo" e tem de
 * reabrir a ficha lendo "20:00", qualquer que seja o fuso do computador dele.
 */
export function localDoInstante(instante: Date, fuso: string): string {
  const deslocamento = deslocamentoDoFuso(fuso, instante);
  const deslocado = new Date(instante.getTime() + deslocamento * 60_000);
  return deslocado.toISOString().slice(0, 16);
}

/**
 * A sigla do fuso naquele instante — `BRT`, `WEST`, `EDT`.
 *
 * Vai ao lado do horário na tela do aluno. Sem ela, "20:00" é uma afirmação sem
 * referência para quem está viajando, e a dúvida ("será que já converteu?") é
 * exatamente o que faz alguém perder a live por precaução ao contrário.
 *
 * Alguns fusos não têm sigla e o runtime devolve `GMT-3`. É uma resposta pior de
 * ler e melhor que nenhuma, então ela passa como está.
 */
export function siglaDoFuso(fuso: string, instante: Date, locale = 'pt-BR'): string {
  const partes = new Intl.DateTimeFormat(locale, {
    timeZone: fuso,
    timeZoneName: 'short',
  }).formatToParts(instante);
  return partes.find((p) => p.type === 'timeZoneName')?.value ?? fuso;
}

export interface HoraNoFuso {
  /** `12/08` — dia e mês, que é o que se lê de relance numa lista. */
  data: string;
  /** `20:00`, 24 horas. */
  hora: string;
  /** `BRT`, `WEST`. Vai colado à hora, e nunca sozinho. */
  sigla: string;
  /** `sábado, 12 de agosto` — para o cabeçalho da ficha, não para a lista. */
  diaPorExtenso: string;
}

/**
 * A hora, quebrada em pedaços — e a formatação mora no core de propósito.
 *
 * As DUAS telas precisam da mesma frase: o painel mostra ao tutor o que ele
 * marcou, e o app do aluno mostra a mesma live no fuso dele. Se cada uma
 * formatasse por conta própria, a que ficasse diferente seria a que faz o aluno
 * chegar na hora errada — e nenhuma das duas pareceria errada olhando sozinha.
 *
 * É o mesmo motivo de `fraseDaLiberacao` viver aqui.
 */
export function formatarNoFuso(instante: Date, fuso: string, locale = 'pt-BR'): HoraNoFuso {
  const data = new Intl.DateTimeFormat(locale, {
    timeZone: fuso,
    day: '2-digit',
    month: '2-digit',
  }).format(instante);

  const hora = new Intl.DateTimeFormat(locale, {
    timeZone: fuso,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(instante);

  const diaPorExtenso = new Intl.DateTimeFormat(locale, {
    timeZone: fuso,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(instante);

  return { data, hora, sigla: siglaDoFuso(fuso, instante, locale), diaPorExtenso };
}

/**
 * A duração, no jeito que se fala: `1h30`, `45min`, `2h`.
 *
 * Nunca "90 minutos": ninguém marca uma live de noventa minutos, marca uma de
 * uma hora e meia.
 */
export function duracaoLegivel(minutos: number): string {
  if (minutos < 60) return `${minutos}min`;
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  return resto === 0 ? `${horas}h` : `${horas}h${String(resto).padStart(2, '0')}`;
}

/** O fuso do navegador de quem está lendo. É neste que o aluno vê a agenda. */
export function fusoDoNavegador(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FUSO_PADRAO;
  } catch {
    return FUSO_PADRAO;
  }
}

/** O fuso existe nesta instalação? Usado para recusar lixo antes de gravar. */
export function fusoValido(fuso: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: fuso });
    return true;
  } catch {
    return false;
  }
}

// ──────────────────────────────────────────────────────────────────── estado

export interface Evento {
  startsAt: Date;
  durationMinutes: number;
}

export function fimDoEvento(e: Evento): Date {
  return new Date(e.startsAt.getTime() + e.durationMinutes * 60_000);
}

export type EstadoDoEvento = 'futuro' | 'agora' | 'passado';

/**
 * Onde este evento está em relação a agora.
 *
 * `agora` é um estado de primeira classe e não um detalhe da tela: é a única
 * coisa que a agenda precisa gritar. Quem abre a área durante a live tem de ver
 * o botão de entrar antes de qualquer outra coisa — depois que a live acaba,
 * nenhum destaque a traz de volta.
 */
export function estadoDoEvento(e: Evento, agora: Date): EstadoDoEvento {
  if (agora.getTime() < e.startsAt.getTime()) return 'futuro';
  if (agora.getTime() < fimDoEvento(e).getTime()) return 'agora';
  return 'passado';
}

/**
 * Quanto falta, em português, sem biblioteca de datas.
 *
 * Só a granularidade que muda decisão: dias enquanto é longe, horas na véspera,
 * minutos na hora. "Em 2 dias" e "em 47 horas" são a mesma informação, e a
 * primeira é a que se lê.
 */
export function faltaQuanto(e: Evento, agora: Date, t: Tradutor = tradutorDe(null)): string {
  const estado = estadoDoEvento(e, agora);
  if (estado === 'agora') return t('agenda.acontecendoAgora');
  if (estado === 'passado') return t('agenda.jaAconteceu');

  const minutos = Math.round((e.startsAt.getTime() - agora.getTime()) / 60_000);
  if (minutos < 1) return t('agenda.comecaEmInstantes');
  /*
   * O singular e o plural são DUAS chaves, e não uma com um `+ 's'`: em
   * alemão «in 1 Minute» e «in 2 Minuten» mudam a palavra, não a letra
   * final, e em francês o espaço antes da unidade não é o mesmo. A frase
   * inteira na chave é o que deixa cada língua escrevê-la como se escreve.
   */
  if (minutos < 60) return t(minutos === 1 ? 'agenda.emMinuto' : 'agenda.emMinutos', { n: minutos });

  const horas = Math.round(minutos / 60);
  if (horas < 24) return t(horas === 1 ? 'agenda.emHora' : 'agenda.emHoras', { n: horas });

  const dias = Math.round(horas / 24);
  return t(dias === 1 ? 'agenda.emDia' : 'agenda.emDias', { n: dias });
}

/**
 * A ordem da agenda: o que está acontecendo, depois o que vem, e o passado por
 * último — este em ordem inversa, porque de trás o que interessa é o recente.
 */
export function ordenarAgenda<T extends Evento>(eventos: T[], agora: Date): T[] {
  const peso: Record<EstadoDoEvento, number> = { agora: 0, futuro: 1, passado: 2 };
  return [...eventos].sort((a, b) => {
    const ea = estadoDoEvento(a, agora);
    const eb = estadoDoEvento(b, agora);
    if (peso[ea] !== peso[eb]) return peso[ea] - peso[eb];
    const dir = ea === 'passado' ? -1 : 1;
    return dir * (a.startsAt.getTime() - b.startsAt.getTime());
  });
}

// ───────────────────────────────────────── a JANELA DA ENTRADA (25/09/2026)

/**
 * ── QUANTO ANTES O BOTÃO DE ENTRAR APARECE ──────────────────────────────────
 *
 * Ordem do dono, 25/09/2026: *«o link libera somente 10 minutos antes com o
 * botao, antes disso aparece so uma caixa com copiar e colar com a url,
 * bloqueado para edicao»*.
 *
 * ## É uma janela de DESENHO, e nunca de segredo
 *
 * O `join_url` continua a viajar na resposta da agenda **sempre** — antes da
 * janela, durante, e depois. A janela decide o **gesto** que a tela oferece: uma
 * caixa de leitura para copiar antes, o botão de entrar dentro. Esconder o
 * endereço no servidor seria um segredo falso, porque ele já sai da casa por
 * dois outros caminhos que ninguém vai revogar: o `.ics` que o aluno leva para a
 * agenda pessoal (`descricaoParaIcs` põe-no na descrição **e** no `LOCATION`) e
 * o e-mail de aviso do encontro. E partiria exatamente o «copiar e colar» que a
 * ordem pede.
 *
 * ## Por que o número mora aqui, e não nas telas
 *
 * Porque são DUAS telas a lê-lo — a do aluno, que decide entre a caixa e o
 * botão, e o painel do tutor, que precisa de dizer ao operador a partir de que
 * hora o aluno entra. Dez escrito duas vezes é o mesmo defeito que
 * `formatarNoFuso` existe para impedir: a que ficasse diferente seria a que faz
 * a pessoa bater na porta fechada, e nenhuma das duas pareceria errada olhando
 * sozinha.
 *
 * ## `estadoDoEvento` NÃO ganhou um quarto estado
 *
 * Poderia ter ganho um `abrindo` entre `futuro` e `agora`, e seria errado: os
 * três estados existentes ordenam a agenda (`ordenarAgenda` pesa-os) e dizem o
 * que aconteceu com o encontro. A janela da entrada não é um estado do encontro
 * — é uma pergunta sobre um dos seus campos, e um evento **sem link** tem janela
 * e não tem entrada. Um quarto peso em `ordenarAgenda` mudaria a ordem da lista
 * de toda a base por causa de um botão.
 */
export const MINUTOS_ANTES_DA_ENTRADA = 10;

/** O instante em que o botão de entrar aparece: o começo menos a janela. */
export function entradaAbertaEm(e: Evento): Date {
  return new Date(e.startsAt.getTime() - MINUTOS_ANTES_DA_ENTRADA * 60_000);
}

/**
 * A entrada está aberta agora?
 *
 * **Aberta da janela até o FIM do encontro**, e não até o começo: quem chega
 * quinze minutos atrasado na live precisa do botão, e uma janela que fechasse no
 * `startsAt` transformaria um atraso comum em «perdi a aula». O fim é o mesmo
 * `fimDoEvento` que decide o estado `agora` — uma segunda conta de fim seria a
 * divergência entre o destaque da lista e o botão dentro dele.
 *
 * Os limites são `>=` na abertura e `<` no fecho, pelo mesmo motivo de
 * `estadoDoEvento`: o instante do fim já é «passado», e ele é fim de um e começo
 * de nada.
 */
export function entradaEstaAberta(e: Evento, agora: Date): boolean {
  return (
    agora.getTime() >= entradaAbertaEm(e).getTime() && agora.getTime() < fimDoEvento(e).getTime()
  );
}

// ─────────────────────────────────────────────────────────────── a validação

export interface EventoCru {
  title: string;
  startsAt: Date;
  durationMinutes: number;
  timezone: string;
  joinUrl?: string | null;
}

/**
 * O que recusa um evento antes de ele existir.
 *
 * Devolve a lista de problemas em português, e não lança: a tela mostra os três
 * de uma vez em vez de a pessoa descobrir um por envio.
 */
export function conferirEvento(e: EventoCru): string[] {
  const erros: string[] = [];

  if (e.title.trim().length < 2) erros.push('O título precisa ter ao menos 2 caracteres.');
  if (e.title.length > 160) erros.push('O título passa de 160 caracteres.');

  if (Number.isNaN(e.startsAt.getTime())) erros.push('A data e a hora de início não são válidas.');

  if (!Number.isInteger(e.durationMinutes)) {
    erros.push('A duração precisa ser um número inteiro de minutos.');
  } else if (e.durationMinutes < DURACAO_MINIMA || e.durationMinutes > DURACAO_MAXIMA) {
    erros.push(
      `A duração precisa ficar entre ${DURACAO_MINIMA} minutos e ${DURACAO_MAXIMA / 60} horas.`,
    );
  }

  if (!fusoValido(e.timezone)) {
    erros.push(`O fuso "${e.timezone}" não existe. Use um nome como America/Sao_Paulo.`);
  }

  if (e.joinUrl) {
    /*
     * A mesma régua do menu da área, e pelo mesmo motivo: este endereço vai
     * para um `href` na tela de todo aluno da área. `javascript:` colado no
     * painel viraria execução de script na sessão de quem clicasse.
     */
    let esquema = '';
    try {
      esquema = new URL(e.joinUrl).protocol;
    } catch {
      erros.push('O link de entrada não é um endereço válido.');
    }
    if (esquema && esquema !== 'http:' && esquema !== 'https:') {
      erros.push('O link de entrada precisa começar com http:// ou https://.');
    }
  }

  return erros;
}

